import type { EquipmentListItem, Paginated, ProductionStageItem } from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { Plus, Search, Ticket } from 'lucide-react';
import { type DragEvent, useRef, useState } from 'react';
import { EquipmentNav, ExpectedDate } from '@/components/equipment-bits';
import { EquipmentFormDialog } from '@/components/equipment-forms';
import { Avatar, Button, ErrorBox, Input, PageHeader, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { useCan } from '@/lib/auth';
import { type EquipmentFilter, equipmentListQuery, equipmentMetaQuery } from '@/lib/equipment';
import { plural } from '@/lib/format';

export interface ProductionSearch {
  q?: string;
}

export function ProductionPage() {
  const search = useSearch({ strict: false }) as ProductionSearch;
  const navigate = useNavigate();
  const can = useCan();
  const meta = useQuery(equipmentMetaQuery);
  const [q, setQ] = useState(search.q ?? '');
  const [creating, setCreating] = useState(false);
  const debounce = useRef<number | undefined>(undefined);
  const filter: EquipmentFilter = { board: true, q: search.q };
  const list = useQuery(equipmentListQuery(filter));

  const onQuery = (value: string) => {
    setQ(value);
    window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(
      () => navigate({ to: '/production', search: { q: value.trim() || undefined }, replace: true }),
      250,
    );
  };

  const total = list.data?.total;
  return (
    <div>
      <PageHeader
        title="Производство стендов"
        description={
          total !== undefined
            ? `В производстве ${total} ${plural(total, ['стенд', 'стенда', 'стендов'])}`
            : undefined
        }
        actions={
          can('production.manage') && (
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden />
              Стенд
            </Button>
          )
        }
      />
      <EquipmentNav />
      {creating && <EquipmentFormDialog onClose={() => setCreating(false)} />}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-[300px]">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-steel"
            aria-hidden
          />
          <Input
            type="search"
            value={q}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Заводской №, плата, модель, клиент"
            aria-label="Поиск стендов"
            className="pl-9"
          />
        </div>
      </div>
      {list.isPending || meta.isPending ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBox error={list.error} />
      ) : meta.error ? (
        <ErrorBox error={meta.error} />
      ) : (
        <Board
          items={list.data.items}
          stages={meta.data.stages}
          filter={filter}
          canMove={can('production.manage')}
        />
      )}
    </div>
  );
}

function StandCard({
  item,
  dragging,
  draggable,
  onDragStart,
  onDragEnd,
}: {
  item: EquipmentListItem;
  dragging: boolean;
  draggable: boolean;
  onDragStart: (e: DragEvent) => void;
  onDragEnd: () => void;
}) {
  const client = item.contact?.fullName ?? item.company?.name;
  const assembled = Boolean(item.assembledAt);
  return (
    <Link
      to="/equipment/$id"
      params={{ id: item.id }}
      draggable={draggable}
      data-item-id={item.id}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={clsx(
        'block rounded-md border border-line bg-surface px-3 py-2.5 transition-shadow hover:border-line-strong hover:shadow-[0_4px_14px_-8px_rgb(24_34_43/0.4)]',
        dragging && 'opacity-40',
      )}
    >
      <p className="flex gap-2 text-[12px] text-steel">
        <span className="font-semibold tabular-nums text-ink">№ {item.serialNo}</span>
        {item.boardId && <span className="ml-auto truncate tabular-nums">плата {item.boardId}</span>}
      </p>
      <p className="mt-0.5 font-medium leading-snug">{item.model?.name ?? item.title}</p>
      {client && <p className="mt-0.5 truncate text-[13px] text-ink-soft">под заказ: {client}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px]">
        <ExpectedDate label="корпус" date={item.caseExpectedAt} assembled={assembled} />
        <ExpectedDate label="плата" date={item.boardExpectedAt} assembled={assembled} />
        {item.openTickets > 0 && (
          <span className="inline-flex items-center gap-1 text-steel" title="Открытые заявки">
            <Ticket className="size-3.5" aria-hidden />
            {item.openTickets}
          </span>
        )}
        <span className="ml-auto">
          {item.assembler ? (
            <span title={`Сборщик: ${item.assembler.fullName}`}>
              <Avatar name={item.assembler.fullName} userId={item.assembler.userId} size={22} />
            </span>
          ) : (
            <span className="text-[12px] text-steel">без сборщика</span>
          )}
        </span>
      </div>
    </Link>
  );
}

function Board({
  items,
  stages,
  filter,
  canMove,
}: {
  items: EquipmentListItem[];
  stages: ProductionStageItem[];
  filter: EquipmentFilter;
  canMove: boolean;
}) {
  const qc = useQueryClient();
  const [dragId, setDragId] = useState<string | null>(null);
  const [target, setTarget] = useState<{ stageId: string; index: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = equipmentListQuery(filter).queryKey;

  const columns = stages.map((stage) => ({
    stage,
    items: items
      .filter((d) => d.stage?.id === stage.id)
      .sort((a, b) => a.sort - b.sort || a.serialNo.localeCompare(b.serialNo)),
  }));

  const move = useMutation({
    mutationFn: (v: { id: string; stageId: string; afterId?: string; beforeId?: string }) =>
      api(`/equipment/${v.id}/move`, { method: 'POST', body: v }),
    onError: (err) => {
      setError(err.message);
      window.setTimeout(() => setError(null), 6000);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['equipment'] }),
  });

  function indexAt(e: DragEvent, column: HTMLElement) {
    const cards = [...column.querySelectorAll<HTMLElement>('[data-item-id]')].filter(
      (el) => el.dataset.itemId !== dragId,
    );
    const i = cards.findIndex((el) => {
      const r = el.getBoundingClientRect();
      return e.clientY < r.top + r.height / 2;
    });
    return i === -1 ? cards.length : i;
  }

  function drop(stage: ProductionStageItem) {
    if (!dragId || !target) return;
    const column = columns.find((c) => c.stage.id === stage.id)?.items.filter((d) => d.id !== dragId) ?? [];
    const after = column[target.index - 1];
    const before = column[target.index];
    const dragged = items.find((d) => d.id === dragId);
    setDragId(null);
    setTarget(null);
    if (!dragged) return;
    const sort =
      after && before
        ? (after.sort + before.sort) / 2
        : after
          ? after.sort + 1
          : before
            ? before.sort - 1
            : dragged.sort;
    qc.setQueryData(key, (old: Paginated<EquipmentListItem> | undefined) =>
      old
        ? {
            ...old,
            items: old.items.map((d) =>
              d.id === dragged.id
                ? {
                    ...d,
                    stage: { id: stage.id, name: stage.name, color: stage.color, kind: stage.kind },
                    sort,
                  }
                : d,
            ),
          }
        : old,
    );
    move.mutate({ id: dragged.id, stageId: stage.id, afterId: after?.id, beforeId: before?.id });
  }

  return (
    <div>
      {error && (
        <div
          role="alert"
          className="mb-3 rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-[13.5px] text-danger"
        >
          {error}
        </div>
      )}
      <div className="-mx-4 overflow-x-auto px-4 pb-2 lg:-mx-8 lg:px-8">
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(230px, 1fr))` }}
        >
          {columns.map((col) => (
            <section
              key={col.stage.id}
              aria-label={col.stage.name}
              onDragOver={(e) => {
                if (!dragId) return;
                e.preventDefault();
                setTarget({ stageId: col.stage.id, index: indexAt(e, e.currentTarget) });
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setTarget(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                drop(col.stage);
              }}
              className={clsx(
                'flex min-h-[360px] flex-col rounded-[var(--radius-panel)] p-2',
                col.stage.kind === 'paused' ? 'bg-sunken/40' : 'bg-sunken/70',
                target?.stageId === col.stage.id && 'outline-2 outline-dashed outline-line-strong',
              )}
            >
              <header className="px-1.5 pb-2 pt-1">
                <div aria-hidden className="mb-2 h-1 rounded-full" style={{ background: col.stage.color }} />
                <div className="flex items-baseline gap-2">
                  <h2 className="min-w-0 truncate text-[13.5px] font-semibold">{col.stage.name}</h2>
                  <span className="text-[13px] tabular-nums text-steel">{col.items.length}</span>
                </div>
                {col.stage.kind === 'ready' && <p className="text-[12px] text-steel">отсюда — отгрузка</p>}
              </header>
              <div className="flex flex-col gap-2">
                {(() => {
                  let visibleIndex = 0;
                  return col.items.map((d) => {
                    const lifted = d.id === dragId && target !== null;
                    const i = lifted ? -1 : visibleIndex++;
                    return (
                      <div key={d.id}>
                        {target?.stageId === col.stage.id && target.index === i && (
                          <div aria-hidden className="mb-2 h-1 rounded bg-signal" />
                        )}
                        <StandCard
                          item={d}
                          draggable={canMove}
                          dragging={dragId === d.id}
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', d.id);
                            setDragId(d.id);
                          }}
                          onDragEnd={() => {
                            setDragId(null);
                            setTarget(null);
                          }}
                        />
                      </div>
                    );
                  });
                })()}
                {target?.stageId === col.stage.id &&
                  dragId &&
                  target.index >= col.items.filter((d) => d.id !== dragId).length && (
                    <div aria-hidden className="h-1 rounded bg-signal" />
                  )}
                {col.items.length === 0 && !target && (
                  <p className="px-1.5 py-6 text-center text-[12.5px] text-steel">Пусто</p>
                )}
              </div>
            </section>
          ))}
        </div>
      </div>
      <p className="mt-2 text-[12.5px] text-steel">
        Перетащите карточку в другую колонку, чтобы сменить этап. Отгрузка клиенту — кнопкой в карточке стенда
        на этапе «
        {stages
          .filter((s) => s.kind === 'ready')
          .map((s) => s.name)
          .join('» или «')}
        ». Красная дата — комплектующие опаздывают. Отгруженные стенды — в «Базе оборудования».
      </p>
    </div>
  );
}
